import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import RichText, { detectDir } from "./RichText";
import LinkEmbed from "./LinkEmbed";
import { Panel, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import { ImagePlus, Send, Pencil, Trash2, Check, X, Trophy } from "lucide-react";
import PostComments from "./PostComments";

function FeedImage({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true; let created = null;
    (async () => {
      try {
        const resp = await api.get(`/files/${path}`, { responseType: "blob" });
        created = URL.createObjectURL(resp.data);
        if (alive) setUrl(created);
      } catch (e) { /* noop */ }
    })();
    return () => { alive = false; if (created) URL.revokeObjectURL(created); };
  }, [path]);
  if (!url) return <div className="font-mono text-[10px] text-[#71717A]">[ LOADING IMAGE… ]</div>;
  return <img src={url} alt="post" className="w-full max-h-[480px] object-contain border border-[#E4E4E7] dark:border-[#2A2A30] bg-[#FAFAFC] dark:bg-[#09090B]" data-testid="feed-image" />;
}

export default function LeagueFeed({ leagueId }) {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const fileRef = useRef();

  const refresh = async () => {
    try {
      const { data } = await api.get(`/leagues/${leagueId}/feed`);
      setPosts(data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 10000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [leagueId]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !file) {
      toast.error("Add some text or an image.");
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("text", text.trim());
      if (file) form.append("image", file);
      await api.post(`/leagues/${leagueId}/feed`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setText(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await refresh();
      toast.success("Posted.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (p) => { setEditingId(p.id); setEditText(p.text); };
  const cancelEdit = () => { setEditingId(null); setEditText(""); };
  const saveEdit = async (id) => {
    try {
      await api.patch(`/feed/${id}`, { text: editText.trim() });
      setEditingId(null); setEditText("");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/feed/${id}`);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="league-feed">
      <form onSubmit={onSubmit} className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#7C3AED] p-5 space-y-3" data-testid="feed-form">
        <textarea
          data-testid="feed-text"
          value={text} dir={detectDir(text) === "rtl" ? "rtl" : "auto"} onChange={(e) => setText(e.target.value)}
          placeholder="Share something with your league…"
          maxLength={4000}
          rows={3}
          className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 text-[#09090B] dark:text-[#F4F4F5] font-body focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] resize-none"
        />
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <label className="cursor-pointer inline-flex items-center gap-2 border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-xs uppercase tracking-widest hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors font-body" data-testid="feed-image-label">
            <input ref={fileRef} data-testid="feed-image-input" type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <ImagePlus className="w-3 h-3" />
            {file ? file.name.slice(0, 24) : "Add image"}
          </label>
          <button data-testid="feed-submit-btn" type="submit" disabled={submitting} className="bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-5 py-2 hover:bg-[#6D28D9] transition-colors disabled:opacity-50 inline-flex items-center gap-2">
            <Send className="w-3 h-3" /> {submitting ? "Posting…" : "Post"}
          </button>
        </div>
      </form>

      {loading ? <TactLoader /> : posts.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm">[ NO POSTS YET — be the first to start a thread. ]</Panel>
      ) : (
        <div className="space-y-3" data-testid="feed-posts">
          {posts.map((p) =>
            p.type === "match_result"
              ? <MatchHighlightCard key={p.id} post={p} canDelete={user?.role === "admin"} onDelete={() => removePost(p.id)} />
              : <RegularPostCard
                  key={p.id}
                  post={p}
                  user={user}
                  editing={editingId === p.id}
                  editText={editText}
                  setEditText={setEditText}
                  onStartEdit={() => startEdit(p)}
                  onCancelEdit={cancelEdit}
                  onSaveEdit={() => saveEdit(p.id)}
                  onDelete={() => removePost(p.id)}
                />
          )}
        </div>
      )}
    </div>
  );
}

function RegularPostCard({ post: p, user, editing, editText, setEditText, onStartEdit, onCancelEdit, onSaveEdit, onDelete }) {
  const mine = p.user_id === user?.id;
  const canMutate = mine || user?.role === "admin";
  return (
    <article className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-5" data-testid={`feed-post-${p.id}`}>
      <header className="flex items-center justify-between mb-3 gap-2">
        <div>
          <div className="font-heading text-lg uppercase">{p.username}</div>
          <div className="font-mono text-[10px] text-[#71717A] tracking-[0.2em]">
            {new Date(p.created_at).toLocaleString()}{p.edited_at ? " · edited" : ""}
          </div>
        </div>
        {canMutate && !editing && (
          <div className="flex gap-1">
            {mine && (
              <button data-testid={`feed-edit-${p.id}`} onClick={onStartEdit} className="border border-[#E4E4E7] dark:border-[#2A2A30] p-2 hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors" title="Edit">
                <Pencil className="w-3 h-3" />
              </button>
            )}
            <button data-testid={`feed-delete-${p.id}`} onClick={onDelete} className="border border-[#E4E4E7] dark:border-[#2A2A30] p-2 hover:border-[#E11D48] hover:text-[#E11D48] transition-colors" title="Delete">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </header>

      {editing ? (
        <div className="space-y-2">
          <textarea
            data-testid={`feed-edit-text-${p.id}`}
            value={editText} onChange={(e) => setEditText(e.target.value)}
            maxLength={4000} rows={3}
            className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-2 text-[#09090B] dark:text-[#F4F4F5] font-body focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] resize-none"
          />
          <div className="flex justify-end gap-2">
            <button data-testid={`feed-edit-cancel-${p.id}`} onClick={onCancelEdit} className="border border-[#E4E4E7] dark:border-[#2A2A30] p-2 hover:border-[#E11D48] hover:text-[#E11D48] transition-colors"><X className="w-3 h-3" /></button>
            <button data-testid={`feed-edit-save-${p.id}`} onClick={onSaveEdit} className="bg-[#7C3AED] text-white p-2 hover:bg-[#6D28D9] transition-colors"><Check className="w-3 h-3" /></button>
          </div>
        </div>
      ) : (
        p.text && <RichText text={p.text} className="text-[#09090B] dark:text-[#F4F4F5] font-body" />
      )}

      {p.text && !editing && (
        <div className="mt-3"><LinkEmbed text={p.text} /></div>
      )}

      {p.image_path && !editing && (
        <div className="mt-3">
          <FeedImage path={p.image_path} />
        </div>
      )}

      <PostComments postId={p.id} />
    </article>
  );
}

function MatchHighlightCard({ post: p, canDelete, onDelete }) {
  const meta = p.match_meta || {};
  const winnerIsHome = meta.winner_id && meta.winner_id === meta.home_id;
  return (
    <article
      className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#7C3AED] p-5 relative overflow-hidden"
      data-testid={`feed-highlight-${p.id}`}
    >
      <div className="absolute -top-10 -right-10 opacity-[0.04] pointer-events-none" aria-hidden>
        <Trophy className="w-48 h-48 text-[#7C3AED]" />
      </div>

      <header className="flex items-center justify-between gap-2 mb-3 relative">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-[#7C3AED] flex items-center justify-center" aria-hidden>
            <Trophy className="w-3.5 h-3.5 text-black" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">
              MATCH RESULT · ROUND {meta.round}{meta.admin_override ? " · ADMIN" : ""}
            </div>
            <div className="font-mono text-[9px] text-[#71717A] tracking-[0.2em] mt-0.5">
              {new Date(p.created_at).toLocaleString()} · ePlay Bot
            </div>
          </div>
        </div>
        {canDelete && (
          <button data-testid={`feed-delete-${p.id}`} onClick={onDelete} className="border border-[#E4E4E7] dark:border-[#2A2A30] p-2 hover:border-[#E11D48] hover:text-[#E11D48] transition-colors" title="Delete">
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </header>

      <div className="grid grid-cols-3 items-center gap-3 text-center my-4 relative">
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">HOME</div>
          <div className={`font-heading text-xl uppercase truncate ${winnerIsHome ? "text-[#7C3AED]" : "text-[#09090B] dark:text-[#F4F4F5]"}`}>
            {meta.home_username}{winnerIsHome && " 🏆"}
          </div>
        </div>
        <div className="font-mono text-4xl sm:text-5xl text-[#09090B] dark:text-[#F4F4F5]">
          {meta.home_score} <span className="text-[#71717A]">:</span> {meta.away_score}
        </div>
        <div className="text-left">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">AWAY</div>
          <div className={`font-heading text-xl uppercase truncate ${meta.winner_id && !winnerIsHome ? "text-[#7C3AED]" : "text-[#09090B] dark:text-[#F4F4F5]"}`}>
            {meta.away_username}{meta.winner_id && !winnerIsHome && " 🏆"}
          </div>
        </div>
      </div>

      {meta.is_draw && (
        <div className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-3 relative">
          [ DRAW · 1 PT EACH ]
        </div>
      )}

      {p.image_path && (
        <div className="mt-3 relative">
          <FeedImage path={p.image_path} />
        </div>
      )}

      <PostComments postId={p.id} />
    </article>
  );
}
